/**
 * useExecutions - Hook for fetching pipeline executions list
 * Provides real execution data for Demo Viewer browsing
 */

import { useQuery } from '@tanstack/react-query';
import { API_ENV } from '@/lib/api-base';

export interface PipelineExecution {
  id: string;
  asset_path: string;
  curriculum_id: string | null;
  lesson_id: string | null;
  scene_id: string | null;
  pipeline_version: string | null;
  status: string;
  total_estimated_cost_usd: number | null;
  actual_cost_usd: number | null;
  started_at: string | null;
  completed_at: string | null;
  error_message: string | null;
  execution_context: any;
  created_at: string;
  updated_at: string;
  brand: string | null;
  user_id: string | null;
  current_stage: string | null;
  progress: any;
  deleted_at: string | null;
  workflow_id: string | null;
  workflow_run_id: string | null;
  workflow_namespace: string | null;
  processing_time_seconds: number | null;
  priority: number | null;
  output_count?: number; // Only present when withOutputCounts=true
}

export interface ExecutionsResponse {
  executions: PipelineExecution[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
    hasMore: boolean;
  };
  requestId: string;
}

interface UseExecutionsOptions {
  limit?: number;
  offset?: number;
  refetchInterval?: number;
  withOutputCounts?: boolean;
}

export function useExecutions(options: UseExecutionsOptions = {}) {
  const { limit = 50, offset = 0, refetchInterval, withOutputCounts = false } = options;

  return useQuery({
    queryKey: ['executions', { limit, offset, withOutputCounts }],
    queryFn: async (): Promise<ExecutionsResponse> => {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
      });

      if (withOutputCounts) {
        params.append('withOutputCounts', 'true');
      }

      const response = await fetch(`${API_ENV.baseUrl}/api/demo/executions?${params}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: Failed to fetch executions`);
      }

      return response.json();
    },
    refetchInterval,
    staleTime: 30 * 1000, // 30 seconds
    retry: 3,
  });
}